import { Alert, Breadcrumb, Button, Card, Form, InputNumber, Select, Space, Spin, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { get, post } from '@/common/services/httpClient';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import { useEffect } from 'react';
import { useAppQuery } from '@/common/hooks/useAppQuery';
import { useAppMutation } from '@/common/hooks/useAppMutation';
import { useQueryClient } from '@tanstack/react-query';

const { Option } = Select;

type CompanyServiceState = {
    companyId?: number | string;
    companyName?: string;
};

type CompanyServiceItem = {
    id?: number | string;
    serviceCategoryId?: number;
    serviceCategoryName?: string;
    isEdit?: boolean;
    items?: Array<{
        serviceCategoryItemId?: number;
        serviceCategoryItemName?: string;
    }>;
    serviceCategoryItemName?: string;
    serviceCategoryItemId?: number;
    serviceCategoryItemIds?: number[];
    serviceItemCode?: string;
    code?: string;
    name?: string;
    [key: string]: unknown;
};

type CompanyServiceRecord = {
    id?: number;
    companyId?: number;
    companyName?: string;
    isEdit?: boolean;
    services?: CompanyServiceItem[];
};

type CompanyServiceSearchApiResponse = {
    data?: CompanyServiceRecord[];
};

type DropdownItem = {
    Id: number;
    Name: string;
    NameAr?: string;
};

type ServiceCategoryItemDropdown = {
    id: number;
    name: string;
    nameAr: string;
};

type CompanyServiceFormValues = {
    records: Array<{
        id?: number;
        companyId?: number;
        companyName?: string;
        services: Array<{
            id?: number;
            serviceCategoryId?: number;
            serviceCategoryItemIds?: number[];
            isEdit?: boolean;
            lockedServiceCategoryItemIds?: number[];
        }>;
    }>;
};

type SaveCompanyServicePayload = {
    companyId: number;
    companyName: string;
    services: Array<{
        serviceCategoryId: number;
        serviceCategoryName: string;
        items: Array<{
            serviceCategoryItemId: number;
            serviceCategoryItemName: string;
        }>;
    }>;
};

interface ServiceRowProps {
    form: ReturnType<typeof Form.useForm<CompanyServiceFormValues>>[0];
    recordIndex: number;
    serviceFieldName: number;
    serviceCategoriesOptions: { label: string; value: number; labelAr?: string }[];
    isLoadingServiceCategories: boolean;
    onRemove: () => void;
    serviceNumber: number;
    isEditable: boolean;
}

const ServiceRow = ({
    form,
    recordIndex,
    serviceFieldName,
    serviceCategoriesOptions,
    isLoadingServiceCategories,
    onRemove,
    serviceNumber,
    isEditable,
}: ServiceRowProps) => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';

    const serviceCategoryId = Form.useWatch(['records', recordIndex, 'services', serviceFieldName, 'serviceCategoryId'], form) as number | undefined;
    const serviceIsEditable = Form.useWatch(['records', recordIndex, 'services', serviceFieldName, 'isEdit'], form) as boolean | undefined;
    const lockedServiceCategoryItemIds = Form.useWatch(
        ['records', recordIndex, 'services', serviceFieldName, 'lockedServiceCategoryItemIds'],
        form,
    ) as number[] | undefined;
    const isServiceLocked = serviceIsEditable === false;
    const canEditMainService = isEditable && !isServiceLocked;
    const canEditSubServices = isEditable && !!serviceCategoryId;
    const lockedItemIdsSet = new Set((lockedServiceCategoryItemIds ?? []).map((id) => Number(id)));

    const { data: serviceCategoryItems = [], isLoading: isLoadingServiceCategoryItems } = useAppQuery<
        { label: string; value: number; labelAr?: string }[]
    >(
        ['companyServiceCategoryItemsDropdown', serviceCategoryId],
        async (): Promise<{ label: string; value: number; labelAr?: string }[]> => {
            const response: { data: ServiceCategoryItemDropdown[] } = await get(`/ServiceCategoryItem/GetItemByServiceCategoryIdDropdown?serviceCategoryId=${serviceCategoryId}`);
            return (response?.data ?? []).map((item) => ({
                label: item.name,
                value: item.id,
                labelAr: item.nameAr,
            }));
        },
        { enabled: !!serviceCategoryId, staleTime: Infinity },
    );

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '32px 1fr auto',
                gap: 12,
                alignItems: 'start',
                padding: '10px 12px',
                background: serviceNumber % 2 === 0 ? '#fafbfd' : '#ffffff',
                borderBottom: '1px solid #f0f2f5',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f6f8fb';
                e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #e1e8f0';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = serviceNumber % 2 === 0 ? '#fafbfd' : '#ffffff';
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            {/* Number Badge */}
            <div
                style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    background: 'linear-gradient(135deg, #0f85ae 0%, #61aeb2 100%)',
                    color: serviceCategoryId ? '#fff' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 600,
                    marginTop: 8,
                    transition: 'all 0.2s ease',
                }}
            >
                {serviceNumber}
            </div>

            {/* Form Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, minWidth: 0 }}>
                <Form.Item
                    name={[serviceFieldName, 'serviceCategoryId']}
                    style={{ marginBottom: 0 }}
                    rules={[
                        { required: true, message: t('missingServiceCategory') },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value) {
                                    return Promise.resolve();
                                }

                                const services = getFieldValue(['records', recordIndex, 'services']) as Array<{ serviceCategoryId?: number }> | undefined;
                                const duplicateFound = services?.some((item, index) => {
                                    if (!item || index === serviceFieldName) {
                                        return false;
                                    }
                                    return item.serviceCategoryId === value;
                                });

                                if (duplicateFound) {
                                    return Promise.reject(new Error(t('This main service is already added')));
                                }

                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <Select
                        placeholder={t('Select main service')}
                        loading={isLoadingServiceCategories}
                        showSearch
                        size="middle"
                        style={{ width: '100%' }}
                        filterOption={(input, option) =>
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        disabled={!canEditMainService}
                        onChange={() => {
                            form.setFieldValue(
                                ['records', recordIndex, 'services', serviceFieldName, 'serviceCategoryItemIds'],
                                [],
                            );
                        }}
                    >
                        {serviceCategoriesOptions.map((item) => (
                            <Option
                                key={item.value}
                                value={item.value}
                                label={isArabic && item.labelAr ? item.labelAr : item.label}
                            >
                                {isArabic && item.labelAr ? item.labelAr : item.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name={[serviceFieldName, 'serviceCategoryItemIds']}
                    style={{ marginBottom: 0 }}
                    rules={[
                        {
                            validator(_, value) {
                                if (Array.isArray(value) && value.length > 0) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error(t('Missing sub service')));
                            },
                        },
                    ]}
                >
                    <Select
                        mode="multiple"
                        placeholder={t('Select sub services')}
                        loading={isLoadingServiceCategoryItems}
                        disabled={!canEditSubServices}
                        showSearch
                        size="middle"
                        maxTagCount={2}
                        maxTagTextLength={20}
                        style={{ width: '100%' }}
                        onDeselect={(value) => {
                            const numericValue = Number(value);
                            if (!isServiceLocked || !lockedItemIdsSet.has(numericValue)) {
                                return;
                            }

                            const fieldPath: ['records', number, 'services', number, 'serviceCategoryItemIds'] = [
                                'records',
                                recordIndex,
                                'services',
                                serviceFieldName,
                                'serviceCategoryItemIds',
                            ];
                            const currentValues = (form.getFieldValue(fieldPath) as number[] | undefined) ?? [];
                            if (!currentValues.includes(numericValue)) {
                                form.setFieldValue(fieldPath, [...currentValues, numericValue]);
                            }
                        }}
                        tagRender={(tagProps) => {
                            const { label, value, closable, onClose } = tagProps;
                            const isLockedTag = isServiceLocked && lockedItemIdsSet.has(Number(value));
                            const effectiveClosable = isLockedTag ? false : closable;

                            return (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        maxWidth: '100%',
                                    }}
                                >
                                    <span
                                        style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {label}
                                    </span>
                                    {effectiveClosable && (
                                        <span
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                            }}
                                            onClick={onClose}
                                            style={{ cursor: 'pointer', lineHeight: 1 }}
                                        >
                                            ×
                                        </span>
                                    )}
                                </span>
                            );
                        }}
                        filterOption={(input, option) =>
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {serviceCategoryItems.map((item) => (
                            <Option
                                key={item.value}
                                value={item.value}
                                label={isArabic && item.labelAr ? item.labelAr : item.label}
                                disabled={isServiceLocked && lockedItemIdsSet.has(Number(item.value))}
                            >
                                {isArabic && item.labelAr ? item.labelAr : item.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </div>

            {/* Delete Button */}
            {canEditMainService && (
                <Button
                    danger
                    type="text"
                    size="small"
                    icon={<DeleteOutlined style={{ color: '#dc2626' }} />}
                    onClick={onRemove}
                    title={t('Delete')}
                    style={{ 
                        marginTop: 4,
                        opacity: 0.6,
                        transition: 'opacity 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                />
            )}
        </div>
    );
};

const CompanyService = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const router = useRouter();
    const [form] = Form.useForm<CompanyServiceFormValues>();

    const state = router?.state?.location?.state as CompanyServiceState | undefined;
    const companyId = state?.companyId;
    const companyName = state?.companyName;

    const parsedCompanyId = Number(companyId);

    const { data: serviceCategoriesOptions = [], isLoading: isLoadingServiceCategories } = useAppQuery(
        ['companyServiceCategoriesDropdown'],
        async (): Promise<{ label: string; value: number; labelAr?: string }[]> => {
            const response = await post<Record<string, DropdownItem[]>>('/Lookup/GetDropdownData', {
                dropdowns: ['servicecategory'],
            });

            const options = response.servicecategory ?? response.serviceCategory ?? [];
            return options.map((item) => ({
                label: item.Name,
                labelAr: item.NameAr,
                value: item.Id,
            }));
        },
        { staleTime: Infinity },
    );

    const { data: records = [], isLoading, isError } = useAppQuery(
        [...QUERY_KEYS.PARTICIPANTS.COMPANY_SERVICE, parsedCompanyId],
        async () => {
            const response = await post<CompanyServiceSearchApiResponse>(
                API_ENDPOINTS.PARTICIPANTS.SEARCH_COMPANY_SERVICE,
                {
                    pageNumber: 0,
                    pageSize: 0,
                    companyId: parsedCompanyId,
                    isExport: false,
                },
            );

            return response?.data ?? [];
        },
        { enabled: Number.isFinite(parsedCompanyId) && parsedCompanyId > 0 },
    );

    // determine whether user is allowed to modify the records. API returns
    // `isEdit: false` when editing shouldn't be permitted.
    const isEditable = !(records.length > 0 && records[0].isEdit === false);

    useEffect(() => {
        if (!records.length) {
            form.setFieldsValue({
                records: [
                    {
                        companyId: parsedCompanyId,
                        companyName,
                        services: [],
                    },
                ],
            });
            return;
        }

        form.setFieldsValue({
            records: records.map((record) => ({
                id: record.id,
                companyId: record.companyId,
                companyName: record.companyName,
                services: (record.services ?? []).map((service) => ({
                    id: typeof service.id === 'number' ? service.id : undefined,
                    serviceCategoryId: service.serviceCategoryId,
                    isEdit: service.isEdit,
                    serviceCategoryItemIds: Array.isArray(service.items)
                        ? service.items
                            .map((item) => Number(item.serviceCategoryItemId ?? 0))
                            .filter((id) => id > 0)
                        : Array.isArray(service.serviceCategoryItemIds)
                            ? service.serviceCategoryItemIds
                            : service.serviceCategoryItemId
                                ? [service.serviceCategoryItemId]
                                : [],
                    lockedServiceCategoryItemIds: service.isEdit === false && Array.isArray(service.items)
                        ? service.items
                            .map((item) => Number(item.serviceCategoryItemId ?? 0))
                            .filter((id) => id > 0)
                        : [],
                })),
            })),
        });
    }, [records, form, parsedCompanyId, companyName]);

    const queryClient = useQueryClient();

    const saveCompanyServiceMutation = useAppMutation<unknown, Error, SaveCompanyServicePayload>(
        (payload) => post(API_ENDPOINTS.PARTICIPANTS.SAVE_COMPANY_SERVICE, payload),
        {
            successMessage: t('Company services saved successfully.'),
            errorMessagePrefix: t('Failed to save company services'),
            invalidateQueryKey: QUERY_KEYS.PARTICIPANTS.COMPANY_SERVICE,
            SecondQueryKey: [QUERY_KEYS.PARTICIPANTS.COMPANY, 'companyServiceCategoriesDropdown'],
            onSuccess: () => {
                // ensure data for this company is refreshed as well
                if (Number.isFinite(parsedCompanyId) && parsedCompanyId > 0) {
                    queryClient.invalidateQueries({ queryKey: ['companyServicesByCompany', parsedCompanyId] });
                    // optionally refetch immediately
                    queryClient.refetchQueries({ queryKey: ['companyServicesByCompany', parsedCompanyId] });
                }
            },
        },
    );

    const buildServiceItemNameMap = async (categoryIds: number[]) => {
        const uniqueCategoryIds = Array.from(new Set(categoryIds.filter((id) => Number.isFinite(id) && id > 0)));
        const pairs = await Promise.all(
            uniqueCategoryIds.map(async (categoryId) => {
                const response: { data: ServiceCategoryItemDropdown[] } = await get(`/ServiceCategoryItem/GetItemByServiceCategoryIdDropdown?serviceCategoryId=${categoryId}`);
                return {
                    categoryId,
                    items: response?.data ?? [],
                };
            }),
        );

        const itemNameMap: Record<string, string> = {};
        pairs.forEach(({ categoryId, items }) => {
            items.forEach((item) => {
                itemNameMap[`${categoryId}-${item.id}`] = item.name;
            });
        });

        return itemNameMap;
    };

    const onFinish = async (values: CompanyServiceFormValues) => {
        try {
            const mergedServices = (values.records ?? []).flatMap((record) => record.services ?? []);

            if (!mergedServices.length) {
                message.warning(t('Please add at least one service.'));
                return;
            }

            const duplicateKeys = new Set<string>();
            for (const service of mergedServices) {
                const categoryId = Number(service.serviceCategoryId ?? 0);
                const itemIds = Array.isArray(service.serviceCategoryItemIds) ? service.serviceCategoryItemIds : [];
                const sortedIds = [...itemIds].map((id) => Number(id)).filter((id) => id > 0).sort((a, b) => a - b);
                const key = `${categoryId}-${sortedIds.join(',')}`;
                if (duplicateKeys.has(key)) {
                    message.warning(t('Duplicate services are not allowed.'));
                    return;
                }
                duplicateKeys.add(key);
            }

            const categoryNameMap = Object.fromEntries(
                serviceCategoriesOptions.map((option) => [Number(option.value), option.label]),
            ) as Record<number, string>;

            const categoryIds = mergedServices
                .map((service) => Number(service.serviceCategoryId ?? 0))
                .filter((id) => id > 0);

            const itemNameMap = await buildServiceItemNameMap(categoryIds);

            const payload: SaveCompanyServicePayload = {
                companyId: parsedCompanyId,
                companyName: companyName || '',
                services: mergedServices.map((service) => {
                    const serviceCategoryId = Number(service.serviceCategoryId ?? 0);
                    const selectedItemIds = Array.isArray(service.serviceCategoryItemIds)
                        ? service.serviceCategoryItemIds.map((id) => Number(id)).filter((id) => id > 0)
                        : [];
                    return {
                        serviceCategoryId,
                        serviceCategoryName: categoryNameMap[serviceCategoryId] || '',
                        items: selectedItemIds.map((serviceCategoryItemId) => ({
                            serviceCategoryItemId,
                            serviceCategoryItemName: itemNameMap[`${serviceCategoryId}-${serviceCategoryItemId}`] || '',
                        })),
                    };
                }),
            };

            await saveCompanyServiceMutation.mutateAsync(payload);
        } catch {
            // handled by useAppMutation
        }
    };

    const handleGoBack = () => {
        navigate({ to: '..' });
    };

    const breadcrumbItems = [
        { label: t('Companies', { defaultValue: 'Companies' }), href: '/participants/company' },
        { label: t('Company Service', { defaultValue: 'Company Service' }) },
    ];

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <Breadcrumb>
                    {breadcrumbItems.map((item, idx) =>
                        item.href ? (
                            <Breadcrumb.Item key={idx}>
                                <a
                                    href={item.href}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate({ to: item.href });
                                    }}
                                >
                                    {item.label}
                                </a>
                            </Breadcrumb.Item>
                        ) : (
                            <Breadcrumb.Item key={idx}>{item.label}</Breadcrumb.Item>
                        ),
                    )}
                </Breadcrumb>
                <Button htmlType="button" onClick={handleGoBack}>
                    {t('common.goBack', { defaultValue: 'Go Back' })}
                </Button>
            </div>

            <Card 
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{t('Company Service')}</span>
                        <span style={{ 
                            fontSize: 13, 
                            fontWeight: 500, 
                            color: '#0f85ae',
                            background: '#e0f2f7',
                            padding: '2px 10px',
                            borderRadius: 6,
                        }}>
                            {companyName || '-'}
                        </span>
                    </div>
                }
                styles={{
                    body: { padding: '20px' }
                }}
            >
                <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 12 }}>
                    {!companyId && (
                        <Alert
                            type="warning"
                            message={t('Company id is missing.')}
                            showIcon
                        />
                    )}
                    {isError && (
                        <Alert
                            type="error"
                            message={t('Failed to load company services.')}
                            showIcon
                        />
                    )}
                </Space>

                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Form form={form} layout="vertical" onFinish={onFinish} scrollToFirstError={{ behavior: 'smooth', block: 'center' }}>
                        <Form.List name="records">
                            {(recordFields) => (
                                <>
                                    {recordFields.map((recordField, recordIndex) => (
                                        <div key={recordField.key}>
                                            <Form.Item name={[recordField.name, 'id']} hidden>
                                                <InputNumber />
                                            </Form.Item>
                                            <Form.Item name={[recordField.name, 'companyId']} hidden>
                                                <InputNumber />
                                            </Form.Item>
                                            <Form.Item name={[recordField.name, 'companyName']} hidden>
                                                <Select />
                                            </Form.Item>

                                            <Form.List name={[recordField.name, 'services']}>
                                                {(serviceFields, { add, remove }) => (
                                                    <div
                                                        style={{
                                                            background: '#fff',
                                                            borderRadius: 8,
                                                            border: '1px solid #e5e7eb',
                                                            overflow: 'hidden',
                                                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                                                        }}
                                                    >
                                                        {/* Table Header */}
                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: '32px 1fr auto',
                                                                gap: 12,
                                                                padding: '10px 12px',
                                                                background: 'linear-gradient(to right, #f9fafb, #f3f4f6)',
                                                                borderBottom: '2px solid #e5e7eb',
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                color: 'rgba(0,0,0,0.88)',
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.5px',
                                                            }}
                                                        >
                                                            <div>#</div>
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                                                <div>{t('Main Service')}</div>
                                                                <div>{t('Sub Services')}</div>
                                                            </div>
                                                            <div style={{ width: 32 }}></div>
                                                        </div>

                                                        {/* Service Rows */}
                                                        {serviceFields.length === 0 ? (
                                                            <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                                                                <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 8 }}>
                                                                    {t('No services added yet.')}
                                                                </div>
                                                                <div style={{ fontSize: 12, color: '#d1d5db' }}>
                                                                    {t('Click the button below to add your first service')}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            serviceFields.map((serviceField, index) => (
                                                                <ServiceRow
                                                                    key={serviceField.key}
                                                                    form={form}
                                                                    recordIndex={recordIndex}
                                                                    serviceFieldName={serviceField.name}
                                                                    serviceCategoriesOptions={serviceCategoriesOptions}
                                                                    isLoadingServiceCategories={isLoadingServiceCategories}
                                                                    onRemove={() => remove(serviceField.name)}
                                                                    serviceNumber={index + 1}
                                                                    isEditable={isEditable}
                                                                />
                                                            ))
                                                        )}

                                                        {/* Add Button */}
                                                        {isEditable && (
                                                            <div style={{ padding: '12px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                                                                <Button
                                                                    type="dashed"
                                                                    onClick={() =>
                                                                        add({
                                                                            serviceCategoryId: undefined,
                                                                            serviceCategoryItemIds: [],
                                                                        })
                                                                    }
                                                                    icon={<PlusOutlined />}
                                                                    block
                                                                    style={{
                                                                        height: 36,
                                                                        borderColor: '#d1d5db',
                                                                        color: '#0891B2',
                                                                        fontWeight: 500,
                                                                    }}
                                                                >
                                                                    {t('Add Service')}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </Form.List>
                                        </div>
                                    ))}
                                </>
                            )}
                        </Form.List>
                        <div 
                            style={{ 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                marginTop: 20,
                                paddingTop: 20,
                                borderTop: '1px solid #e5e7eb',
                            }}
                        >
                            <Space size={12}>
                                <Button 
                                    onClick={handleGoBack}
                                    size="large"
                                    style={{
                                        minWidth: 100,
                                        borderRadius: 8,
                                        fontWeight: 500,
                                    }}
                                >
                                    {t('Cancel')}
                                </Button>
                                {isEditable && (
                                    <Button 
                                        type="primary" 
                                        htmlType="submit" 
                                        loading={saveCompanyServiceMutation.isPending}
                                        size="large"
                                        style={{
                                            minWidth: 140,
                                            borderRadius: 8,
                                            fontWeight: 600,
                                            background: 'linear-gradient(135deg, #0f85ae 0%, #61aeb2 100%)',
                                            border: 'none',
                                            boxShadow: '0 4px 12px rgba(15, 133, 174, 0.3)',
                                        }}
                                    >
                                        {t('Save')}
                                    </Button>
                                )}
                            </Space>
                        </div>
                    </Form>
                )}
            </Card>
        </>
    );
};

export default CompanyService;
